import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { fetchGameSessions } from '../../api/gameSessions';

interface Props {
  open: boolean;
  gameId: string | null;
  gameName: string;
  onClose: () => void;
}

type RangeOption = '1d' | '7d' | '30d';

function rangeLabel(r: RangeOption): string {
  return r === '1d' ? 'Letzte 24h' : r === '7d' ? 'Letzte 7 Tage' : 'Letzte 30 Tage';
}

function rangeDays(r: RangeOption): number {
  return r === '1d' ? 1 : r === '7d' ? 7 : 30;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

export function SessionHistoryDialog({ open, gameId, gameName, onClose }: Props) {
  const [range, setRange] = useState<RangeOption>('7d');

  const now = new Date();
  const from = new Date(now.getTime() - rangeDays(range) * 86_400_000).toISOString();
  const to = now.toISOString();

  const { data, isLoading } = useQuery({
    queryKey: ['game-sessions', gameId, range],
    queryFn: () => fetchGameSessions(gameId!, from, to),
    enabled: open && gameId !== null,
    staleTime: 60_000,
  });

  const players = data?.players ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <span>Session-Historie — {gameName}</span>
          <Select
            value={range}
            onChange={(e) => setRange(e.target.value as RangeOption)}
            size="small"
            sx={{ minWidth: 140 }}
          >
            {(['1d', '7d', '30d'] as RangeOption[]).map((r) => (
              <MenuItem key={r} value={r}>{rangeLabel(r)}</MenuItem>
            ))}
          </Select>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && players.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Keine Spieler-Daten im gewählten Zeitraum.
          </Typography>
        )}
        {!isLoading && players.length > 0 && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Spieler</TableCell>
                  <TableCell>Zuletzt gesehen</TableCell>
                  <TableCell align="right">Gesamt (ca.)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {players.map((p, i) => (
                  <TableRow key={i}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{fmtDate(p.last_seen)}</TableCell>
                    <TableCell align="right">{fmtDuration(p.total_duration_s)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Namen basieren auf dem In-Game-Namen zum Abfragezeitpunkt. Spielzeit ist näherungsweise.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}
